import React, { useEffect, useState } from 'react'
import BackendApis from '../../utils/backendApis'

// BaseModal 컴포넌트
const BaseModal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black bg-opacity-50'
        onClick={onClose}
        aria-hidden='true'
      />
      <div className='bg-white rounded-lg w-full max-w-md mx-4 z-10 overflow-hidden shadow-lg'>
        <div className='flex justify-between items-center p-4 border-b border-[#EEEFF3]'>
          <h2 className='font-medium text-base text-[#30333D]'>{title}</h2>
          <button
            type='button'
            onClick={onClose}
            className='text-[#9C9DA4] hover:text-[#30333D] transition-colors'
            aria-label='닫기'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-5 w-5'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

// ShippingModal 컴포넌트
const ShippingModal = ({ isOpen, onClose, orderId }) => {
  const [trackingDetail, setTrackingDetail] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [shippingInfo, setShippingInfo] = useState(null)

  useEffect(() => {
    const fetchShippingInfo = async () => {
      if (!isOpen || !orderId) return

      setIsLoading(true)
      try {
        const result = await BackendApis.getShippingInfo([orderId])
        if (result?.data) {
          setShippingInfo(result.data)
          setTrackingDetail(result.data.trackingDetail || [])
        }
      } catch (error) {
        console.error('배송 정보 조회 중 오류 발생:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchShippingInfo()
  }, [isOpen, orderId])

  const formatDate = (dateString) => {
    if (!dateString) return '-'

    try {
      const date = new Date(dateString)
      const month = date.getMonth() + 1
      const day = date.getDate()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')

      return `${month}/${day} ${hours}:${minutes}`
    } catch {
      return dateString
    }
  }

  const styles = {
    container: 'p-6',
    infoBox: 'mb-6 bg-[#f8f9fa] p-5 rounded-lg',
    infoRow: 'flex items-center mb-3 last:mb-0',
    infoLabel: 'w-24 text-[#555555] text-base font-medium',
    infoValue: 'text-[#333333] text-base',
    sectionTitle: 'text-[#333333] text-lg font-semibold mb-3',
    tableContainer: 'rounded-lg overflow-hidden border border-[#e9ecef]',
    tableHeader: 'grid grid-cols-3 bg-[#f1f3f5] border-b border-[#e9ecef]',
    tableRow: 'grid grid-cols-3 border-b border-[#e9ecef] last:border-b-0',
    tableCell: 'py-3 px-4 text-[#495057] text-base text-center',
    emptyState:
      'p-4 text-[#6c757d] bg-[#f8f9fa] rounded-lg text-center text-base',
    footerText: 'text-[#6c757d] text-base',
    loadingState: 'p-6 text-center text-[#6c757d] text-base',
  }

  const renderTrackingTable = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>배송 정보를 불러오는 중...</div>
      )
    }

    if (!trackingDetail.length) {
      return <div className={styles.emptyState}>배송 정보가 없습니다.</div>
    }

    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          {['시간', '위치', '상태'].map((header) => (
            <div key={header} className={styles.tableCell}>
              {header}
            </div>
          ))}
        </div>

        {trackingDetail.map((detail) => {
          const key = `${detail.timeString || ''}-${detail.where || ''}-${
            detail.kind || ''
          }`
          return (
            <div key={key} className={styles.tableRow}>
              <div className={styles.tableCell}>
                {formatDate(detail.timeString)}
              </div>
              <div className={styles.tableCell}>{detail.where || '-'}</div>
              <div className={styles.tableCell}>{detail.kind || '-'}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title='배송조회'>
      <div className='relative flex flex-col h-[80vh]'>
        <div className='flex-1 overflow-y-auto'>
          <div className={styles.container}>
            <div className={styles.infoBox}>
              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>송장 번호</div>
                <div className={styles.infoValue}>
                  {shippingInfo?.shippingNumber || '추후 추가예정'}
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>택배사</div>
                <div className={styles.infoValue}>
                  {shippingInfo?.shippingCompanyName || 'CJ대한통운'}
                </div>
              </div>
            </div>

            <div className='mb-6'>
              <h3 className={styles.sectionTitle}>배송 현황</h3>
              {renderTrackingTable()}
            </div>

            {shippingInfo?.shippingStartedAt && (
              <div className={styles.footerText}>
                <span>배송 시작 시간:</span>{' '}
                {formatDate(shippingInfo.shippingStartedAt)}
              </div>
            )}
          </div>
        </div>
        <div className='flex justify-end p-4 border-t border-[#e9ecef] bg-white'>
          <button
            type='button'
            onClick={onClose}
            className='px-6 py-2 text-base font-medium text-[#4B5563] bg-[#F3F4F6] rounded-md hover:bg-[#E5E7EB] transition-colors'
          >
            닫기
          </button>
        </div>
      </div>
    </BaseModal>
  )
}

export default ShippingModal
